import * as Validator from "yup"
import { imageFileValidator } from "../shared/utils"
import type { IHotelAddOnFormSchema } from "./NewItem"
import type { THotelAddon } from "./store"

export const TAX_PERCENTAGE_PRESETS: Array<
	Exclude<IHotelAddOnFormSchema["tax_type"], "custom">
> = ["0", "5", "18"]

export const emptyHotelAddOnFormValues: IHotelAddOnFormSchema = {
	name: "",
	description: "",
	code: "",
	rate_before_tax: undefined,
	tax_type: "custom",
	custom_tax_percentage: undefined,
	image: undefined,
	is_service_date_flexible: 0,
}

// Checked on the typed value, as the parsed number can't tell "1.50" from "1.5"
function hasAtMostDecimals(digits: number) {
	return function (
		value: number | undefined,
		context: Validator.TestContext
	): boolean {
		if (value === undefined) return true
		const [, decimals = ""] = String(context.originalValue).split(".")
		return decimals.length <= digits
	}
}

export function hotelAddOnValidationSchema({
	requireImage,
	requireCode,
}: {
	requireImage: boolean
	// a saved add-on always has a code, which can be changed but not cleared
	requireCode: boolean
}) {
	const code = Validator.string()
		.trim()
		.max(20, "Please use 20 or less characters")
	return Validator.object().shape({
		name: Validator.string()
			.trim()
			.min(2, "Should be at least 2 characters")
			.max(191, "Please use 191 or less characters")
			.required("Name is required"),
		description: Validator.string().max(
			2000,
			"Please use 2000 or less characters"
		),
		code: requireCode
			? code.required("Code is required")
			: code.transform((value) => value || undefined),
		rate_before_tax: Validator.number()
			.typeError("Please enter a valid amount")
			.required("Base rate is required")
			.min(0, "Must be >= 0")
			.test(
				"max-3-decimals",
				"Please use 3 or less decimals",
				hasAtMostDecimals(3)
			),
		tax_type: Validator.mixed().oneOf([...TAX_PERCENTAGE_PRESETS, "custom"]),
		custom_tax_percentage: Validator.number().when(
			"tax_type",
			([taxType], schema) =>
				taxType === "custom"
					? schema
							.typeError("Please enter a valid tax %")
							.required("Tax % is required")
							.min(0, "Must be >= 0")
							.max(100, "Must be <= 100")
							.test(
								"max-2-decimals",
								"Please use 2 or less decimals",
								hasAtMostDecimals(2)
							)
					: schema.notRequired()
		),
		image: imageFileValidator(
			requireImage ? "Please select an image" : undefined
		),
	})
}

export function hotelAddOnToFormValues(
	addOn: THotelAddon
): IHotelAddOnFormSchema {
	const taxPreset = TAX_PERCENTAGE_PRESETS.find(
		(preset) => Number(preset) === Number(addOn.tax_percentage)
	)
	return {
		name: addOn.name,
		description: addOn.description || "",
		code: addOn.code || "",
		rate_before_tax: addOn.rate_before_tax,
		tax_type: taxPreset || "custom",
		is_service_date_flexible: addOn.is_service_date_flexible ? 1 : 0,
		custom_tax_percentage: taxPreset ? undefined : String(addOn.tax_percentage),
		image: undefined,
	}
}

export function getTaxPercentage(
	values: Pick<IHotelAddOnFormSchema, "tax_type" | "custom_tax_percentage">
): number | undefined {
	const taxPercentage =
		values.tax_type === "custom"
			? values.custom_tax_percentage
			: values.tax_type
	if (taxPercentage == null || taxPercentage === "") return undefined
	return Number.isFinite(Number(taxPercentage))
		? Number(taxPercentage)
		: undefined
}

// Empty strings are read as null by the api
export function hotelAddOnFormValuesToFormData(values: IHotelAddOnFormSchema) {
	const data = new FormData()
	data.append("name", values.name.trim())
	data.append("description", (values.description || "").trim())
	data.append("code", (values.code || "").trim())
	data.append("rate_before_tax", String(values.rate_before_tax))
	data.append("tax_percentage", String(getTaxPercentage(values)))
	// required by the api on create and update, but not managed by the form yet
	data.append("price_mode", "per_quantity")
	data.append("applicable_on", "custom_days")
	data.append("min_child_age", "0")
	data.append(
		"is_service_date_flexible",
		String(values.is_service_date_flexible)
	)
	// only a newly picked image, so an edit keeps the saved one
	if (values.image instanceof File) {
		data.append("image", values.image)
	}
	return data
}
